import { ReactNode, useEffect, useState } from 'react';
import { Card } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { RefreshCw } from 'lucide-react';
import { useDashboardContext } from '../hooks/DashboardContext';
import { WidgetConfig } from '../contracts/widget.contract';
import { WidgetLoading, WidgetError, WidgetPermissionDenied } from './WidgetStates';
import { useRole } from '@/shared/contexts/RoleContext';

interface WidgetContainerProps {
  config: WidgetConfig;
  children: ReactNode;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  onRefresh?: () => Promise<void> | void;
  actions?: ReactNode;
}

export function WidgetContainer({
  config,
  children,
  isLoading,
  isError,
  errorMessage,
  onRefresh,
  actions,
}: WidgetContainerProps) {
  const { can } = useRole();
  const { registerWidgetRefresh, unregisterWidgetRefresh } = useDashboardContext();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Lifecycle Registration
  useEffect(() => {
    if (onRefresh) {
      const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
          await onRefresh();
        } finally {
          setIsRefreshing(false);
        }
      };
      registerWidgetRefresh(config.id, handleRefresh);
      
      if (config.refreshStrategy === 'interval' && config.refreshIntervalMs) {
        const intervalId = setInterval(handleRefresh, config.refreshIntervalMs);
        return () => {
          clearInterval(intervalId);
          unregisterWidgetRefresh(config.id);
        };
      }

      return () => unregisterWidgetRefresh(config.id);
    }
  }, [config.id, config.refreshStrategy, config.refreshIntervalMs, onRefresh, registerWidgetRefresh, unregisterWidgetRefresh]);

  // Permission Check
  if (config.permission && !can(config.permission)) {
    return (
      <Card className="flex h-full flex-col overflow-hidden">
        <div className="flex flex-1 items-center justify-center p-6 text-center">
          <WidgetPermissionDenied />
        </div>
      </Card>
    );
  }

  const handleManualRefresh = async () => {
    if (onRefresh) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  return (
    <Card className="flex h-full flex-col overflow-hidden transition-shadow duration-150 ease-standard hover:shadow-surface-2">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b border-outline px-4 py-3">
        <div className="min-w-0">
          <h3 className="truncate text-data-lg font-semibold text-on-surface">{config.title}</h3>
          {config.description && (
            <p className="truncate text-body-sm text-on-surface-variant">{config.description}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {actions}
          {onRefresh && config.refreshStrategy !== 'disabled' && (
            <Button
              variant="ghost"
              size="icon"
              onClick={handleManualRefresh}
              disabled={isRefreshing || isLoading}
              aria-label="Refresh widget"
            >
              <RefreshCw className={`h-4 w-4 text-on-surface-variant ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="relative flex-1 overflow-auto p-4">
        {isLoading && !isRefreshing ? (
          <WidgetLoading />
        ) : isError ? (
          <WidgetError message={errorMessage} onRetry={handleManualRefresh} />
        ) : (
          children
        )}
        
        {/* Refreshing Overlay */}
        {isRefreshing && !isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface/50 backdrop-blur-[1px]">
            <RefreshCw className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}
      </div>
    </Card>
  );
}
